import React, { forwardRef } from 'react';
import { Typography, Tooltip, Checkbox, Box, Stack } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { useStyles } from '../UrbanSportsConnect/ConnectUrbanSports.styles';
import { useTranslation } from 'react-i18next';
import { LocationStatusTag } from '~/Components/LocationStatusTag';
import { ILocationWithStatus } from '~/Models/interfaces/Location.model';
import { LocationStatus } from '~/Models/DTO/externalParties.model';
import { PRIMARY_BLUE, DRAWER_INACTIVE_SELECTION } from '~/Constants/Colors.constants';

interface Location {
  id: string;
  uuid: string;
  providerId?: string;
  name: string;
  street: string;
  city: string;
  zipCode: string;
  status?: LocationStatus | null;
  integrationStatus?: string;
}

interface Integration {
  location_id: string;
  status: string;
  integration: string;
  id: string;
}

interface LocationListItemProps {
  location: Location;
  locationData: ILocationWithStatus[] | undefined;
  integrations: Integration[] | undefined;
  handleCopyToken: (token: string) => void;
  showCopied: boolean;
  selectedIntegrationId: string;
}

export const LocationListItem = forwardRef<HTMLDivElement, LocationListItemProps>(
  (
    { location, locationData, integrations, selectedIntegrationId, handleCopyToken, showCopied },
    ref,
  ) => {
    const { classes } = useStyles();
    const { t } = useTranslation();

    const matchingLocation = locationData?.find((loc) => loc.uuid === location.uuid);
    const status = matchingLocation?.status || null;

    const locationIntegration = integrations?.find(
      (integration) =>
        integration.location_id === location.uuid && integration.id === selectedIntegrationId,
    );

    const hasChannel = Boolean(locationIntegration);
    const isActive = locationIntegration?.status === 'active';
    const isInactive = locationIntegration?.status === 'inactive';

    const isChecked = hasChannel;
    const isDisabled = !hasChannel || isInactive;
    const checkboxColor = isActive ? 'primary' : 'default';

    let statusMessage = '';
    if (!hasChannel) {
      statusMessage = t('app.noChannelForIntegration');
    } else if (isActive) {
      statusMessage = t('app.activeChannelForIntegration');
    } else if (isInactive) {
      statusMessage = t('app.inactiveChannelForIntegration');
    }

    return (
      <li className={classes.listItem}>
        <Box display="flex" flexDirection="column">
          <Typography className={classes.listItemTitle} variant="body2">
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <span>{location.name}</span>
              <LocationStatusTag status={status} />
            </Stack>
          </Typography>
          <Box display="flex" alignItems="center">
            <Checkbox
              checked={isChecked}
              disabled={isDisabled}
              color={checkboxColor}
              sx={{
                color: isActive ? PRIMARY_BLUE : DRAWER_INACTIVE_SELECTION,
                '&.Mui-checked': {
                  color: isActive ? PRIMARY_BLUE : DRAWER_INACTIVE_SELECTION,
                },
                '&.Mui-disabled': {
                  color: DRAWER_INACTIVE_SELECTION,
                },
              }}
            />
            <Typography variant="body2">{t('app.sync.booking.flow')}</Typography>
            <Typography variant="caption" color="textSecondary" ml={1}>
              {statusMessage}
            </Typography>
          </Box>
        </Box>
        <div className={classes.token} ref={ref}>
          <span>{location.uuid}</span>
          <Tooltip
            title={showCopied ? t('app.copied') : t('app.copy')}
            enterDelay={70}
            leaveDelay={50}
          >
            <ContentCopy
              className={classes.copyIcon}
              onClick={() => handleCopyToken(location.uuid)}
              fontSize="small"
            />
          </Tooltip>
        </div>
      </li>
    );
  },
);
